import {
  useFetchAllUsers,
  useRequestPasswordReset,
} from "../../api/legacy/LegacyUserAPI";
import { LegacyUserWithCompanyName } from "../../api/legacy/LegacyUser";
import Input from "../../shared-components/Input";
import Button from "../../shared-components/Button";
import { useState, useEffect, useCallback } from "react";
import { useApplicationToast } from "../../webcontent/ApplicationToastProvider";
import LegacyUserCreate from "./LegacyUserCreate";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { ButtonColorTypes } from "../../shared-components/Button";
import { classNames } from "../../utils";

function LegacyUserList() {
  const { data, isLoading, error } = useFetchAllUsers();
  const [search, setSearch] = useState("");
  const resetPasswordMutation = useRequestPasswordReset();
  const { showLargeToast } = useApplicationToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [users, setUsers] = useState<LegacyUserWithCompanyName[] | undefined>(
    undefined
  );
  const permissionValidator = usePermissionValidator();
  const canCreateUser = permissionValidator.legacy?.canCreateUsers;

  useEffect(() => {
    if (data) setUsers(data);
  }, [data]);

  const handleUserCreated = useCallback(
    (user: { companyId: string; username: string; email: string }) => {
      const companyName =
        users?.find((u) => u.ossa_company_uuid === user.companyId)
          ?.company_name || "";
      setUsers((prev) =>
        prev
          ? [
              ...prev,
              {
                ossa_company_uuid: user.companyId,
                username: user.username,
                email: user.email,
                company_name: companyName,
              },
            ]
          : prev
      );
      showLargeToast?.({
        title: "Success",
        message: "User successfully created",
        timeout: 5000,
      });
    },
    [users, showLargeToast]
  );

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div>Error loading users.</div>;
  if (!users) return <div>No users found.</div>;

  const groupedByCompanyName: Record<string, LegacyUserWithCompanyName[]> = {};
  users.forEach((user: LegacyUserWithCompanyName) => {
    if (!groupedByCompanyName[user.company_name])
      groupedByCompanyName[user.company_name] = [];
    groupedByCompanyName[user.company_name].push(user);
  });

  Object.values(groupedByCompanyName).forEach((users) => {
    users.sort((a, b) => a.username.localeCompare(b.username));
  });

  const filteredGrouped = Object.entries(groupedByCompanyName).reduce(
    (acc, [companyName, users]) => {
      const filtered = users.filter((u) => {
        const searchLower = search.toLowerCase();
        return (
          u.username.toLowerCase().includes(searchLower) ||
          (u.email || "").toLowerCase().includes(searchLower) ||
          (u.company_name || "").toLowerCase().includes(searchLower)
        );
      });
      if (filtered.length > 0) acc[companyName] = filtered;
      return acc;
    },
    {} as Record<string, LegacyUserWithCompanyName[]>
  );

  return (
    <>
      <div
        className={classNames(
          "relative flex flex-row flex-wrap gap-2 mb-2 bg-white p-4 rounded-lg border border-gray-200 items-center",
          isLoading ? "opacity-50 pointer-events-none" : ""
        )}
      >
        <div className="flex w-full sm:w-auto gap-4">
          <Input
            placeholder="Search by company, username, or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-80"
          />
          {canCreateUser && (
            <Button
              text="Create User"
              onClick={() => setIsCreateOpen(true)}
              className="hover:bg-green-500"
              colorType={ButtonColorTypes.primary}
            />
          )}
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm w-full">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Company Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Username
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Email
              </th>
              {canCreateUser && (
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {Object.entries(filteredGrouped).length === 0 && (
              <tr>
                <td
                  colSpan={canCreateUser ? 4 : 3}
                  className="text-center py-8 text-gray-400"
                >
                  No users found.
                </td>
              </tr>
            )}
            {Object.entries(filteredGrouped).map(([companyId, users]) =>
              users.map((user, idx) => {
                const key = companyId + user.username;
                return (
                  <tr
                    key={key}
                    className={
                      idx % 2 === 0
                        ? "bg-white hover:bg-blue-50 transition"
                        : "bg-gray-50 hover:bg-blue-50 transition"
                    }
                  >
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                      {companyId}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold">
                      {user.username}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                      {user.email || ""}
                    </td>
                    {canCreateUser && (
                      <td className="px-6 py-3 whitespace-nowrap text-sm">
                        <Button
                          text={
                            resetPasswordMutation.isLoading &&
                            resetPasswordMutation.variables === user.username
                              ? "Sending..."
                              : "Reset Password"
                          }
                          onClick={async () => {
                            try {
                              await resetPasswordMutation.mutateAsync(
                                user.username
                              );
                              showLargeToast?.({
                                title: "Success",
                                message: "Password reset request sent!",
                                timeout: 5000,
                              });
                            } catch (err: any) {
                              const detail = err?.response?.data?.detail;
                              showLargeToast?.({
                                title: "Password Reset Failed",
                                message:
                                  detail ||
                                  err?.message ||
                                  "Error sending request",
                                timeout: 5000,
                              });
                            }
                          }}
                          disabled={resetPasswordMutation.isLoading}
                          className="hover:bg-green-500"
                          colorType={ButtonColorTypes.primary}
                        />
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <LegacyUserCreate
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleUserCreated}
      />
    </>
  );
}

export default LegacyUserList;
